// /download, after Epic's download page: a hero with the button for the computer you're on and the other systems as
// secondary links, a picture of the app (an HTML copy of its Home: the real screenshots predate the Banger name), the
// one-line installer, what you get, what it needs, and questions. Every button comes from /downloads/latest.txt
// (downloads.ts): a system without a published build says "coming soon" and links nowhere.
import { artUrl, gameById, getCatalogue, isComingSoon, label, type Game } from "../catalogue.ts";
import { coverStyle } from "../components.ts";
import { h, link, toast } from "../dom.ts";
import { archLabel, fileName, formatBytes, loadBuilds, offers, type Offer } from "../downloads.ts";
import { icon, type IconName } from "../icons.ts";
import { detectPlatform, gameActionLink, launch, playLink, type GameAction } from "../play.ts";
import { setTitle } from "../router.ts";
import { selling } from "../selling.ts";

/** What `?for=` asks the app to do. Tips only while coins can be sold (wave 9); a coming-soon game means its waitlist. */
export function downloadAction(forParam: string | null, comingSoon: boolean, isOn: boolean): GameAction | null {
  if (forParam === "tip" && isOn) return "tip";
  return forParam === "notify" || comingSoon ? "notify" : null;
}

/** The FAQ's "Is Banger free?" answer: no prices are mentioned while nothing is sold. */
export function freeAnswer(isOn: boolean): string {
  return isOn
    ? "Yes. The app is free to download and use. Games show their price before you play, and today every one of them is free."
    : "Yes. The app is free to download and use, and so is every game on it.";
}

/** Where Play lands when no app answered the thrdm:// link, and the place to get the app. */
export async function downloadPage(_p: unknown, query: URLSearchParams): Promise<HTMLElement> {
  setTitle("Download");
  const [cat, builds] = await Promise.all([getCatalogue().catch(() => null), loadBuilds()]);
  const gameId = query.get("game");
  const game = gameId && cat ? gameById(cat, gameId) : undefined;
  const mine = detectPlatform(navigator.userAgent);
  const all = offers(builds);
  const primary = all.find((o) => o.platform === mine) ?? all[0]!;
  const others = all.filter((o) => o !== primary);
  // a coming-soon game can't be played yet: the app can only put you on its waitlist
  const forParam = query.get("for");
  const action = downloadAction(forParam, !!game && isComingSoon(game), selling());
  const heading = !game
    ? "Get Banger"
    : action === "tip"
      ? `Tip the creator of ${game.title} in Banger`
      : action === "notify"
        ? `Get told when ${game.title} is out`
        : `Play ${game.title} in Banger`;

  return h(
    "div",
    { class: "download" },
    h(
      "section",
      { class: "dl-hero" },
      h(
        "div",
        { class: "dl-copy" },
        h("img", { class: "dl-mark", src: "/mark/banger-key-128.webp", srcset: "/mark/banger-key-128.webp 1x, /mark/banger-key-256.webp 2x", alt: "", width: 112, height: 112 }),
        h("p", { class: "eyebrow" }, mine === "mobile" || mine === "other" ? "The Banger app" : `Banger for ${primary.label}`),
        h("h1", null, heading),
        h("p", { class: "lead" }, "Your games, friends and library in one app. Install it once; after that every Play button on this site opens the game in it."),
        mine === "mobile" ? h("p", { class: "note" }, "Banger runs on computers for now. Browse here on your phone, follow creators, and play when you're at your desk.") : null,
        primaryButton(primary),
        h(
          "p",
          { class: "dl-others" },
          "Also for ",
          others.map((o, i) => [i ? " and " : "", otherLink(o)]),
          ".",
        ),
        game ? alreadyInstalled(game, action) : null,
      ),
      h("div", { class: "dl-shot" }, appMock(cat?.lists.find((l) => l.style === "hero")?.games.map((id) => gameById(cat, id)).filter((g): g is Game => !!g) ?? [])),
    ),
    installer(all),
    features(),
    requirements(all),
    faq(),
  );
}

function primaryButton(o: Offer): HTMLElement {
  if (o.builds.length === 0)
    return h(
      "div",
      { class: "dl-primary" },
      h("span", { class: "btn btn-big disabled", "aria-disabled": "true" }, icon("download", 20), `${o.label}: coming soon`),
      h("p", { class: "dim small" }, `The first public build for ${o.label} isn't out yet. This button lights up the day it is.`),
    );
  return h(
    "div",
    { class: "dl-primary" },
    o.builds.map((b) =>
      h("a", { class: "btn btn-big", href: b.url, download: "" }, icon("download", 20), o.builds.length > 1 ? `Download ${fileName(b.url)}` : `Download for ${o.label}`),
    ),
    h(
      "p",
      { class: "dim small" },
      o.builds.map((b) => `${archLabel(b.platform)} · ${b.format} · ${formatBytes(b.bytes)}`).join("; "),
      ". SHA-256 is published next to each file (",
      o.builds.map((b, i) => [i ? ", " : "", h("a", { href: `${b.url}.sha256` }, `${fileName(b.url)}.sha256`)]),
      ").",
    ),
  );
}

function otherLink(o: Offer): HTMLElement {
  if (o.builds.length === 0) return h("span", { class: "dl-soon" }, `${o.label} (coming soon)`);
  return o.builds.length === 1 ? h("a", { href: o.builds[0]!.url, download: "" }, o.label) : h("a", { href: "#requirements" }, o.label);
}

function alreadyInstalled(game: Game, action: GameAction | null): HTMLElement {
  const href = action ? gameActionLink(game.id, action) : playLink(game.id);
  const text = action === "tip" ? "Tip in Banger" : action === "notify" ? "Join the waitlist in Banger" : `Open ${game.title} in Banger`;
  return h(
    "p",
    { class: "small" },
    "Already installed? ",
    h(
      "a",
      {
        href,
        onclick: (e: Event) => {
          e.preventDefault();
          launch(game.id, { link: href });
        },
      },
      text,
    ),
    " · ",
    link(`/g/${game.id}`, null, "Back to the game"),
  );
}

/** The one-liner for macOS and Linux, with a copy button and the script one click away. */
function installer(all: Offer[]): HTMLElement {
  const cmd = `curl -fsSL ${location.origin}/install.sh | sh`;
  const ready = all.filter((o) => o.platform !== "windows" && o.builds.length > 0).map((o) => o.label);
  const copy = h(
    "button",
    {
      type: "button",
      class: "copy",
      "aria-label": "Copy the install command",
      onclick: async () => {
        try {
          await navigator.clipboard.writeText(cmd);
          toast("Copied");
        } catch {
          toast("Couldn't copy; select the command instead");
        }
      },
    },
    icon("copy", 16),
    h("span", null, "Copy"),
  );
  return h(
    "section",
    { class: "dl-install", "aria-labelledby": "dl-install-h" },
    h("h2", { id: "dl-install-h" }, icon("terminal", 20), "Or install from a terminal"),
    h("p", { class: "dim" }, "On macOS and Linux. It works out your system, downloads the right build over https and checks its SHA-256. On a Mac it installs the app for you alone, with no admin password; on Linux it prints the one apt command that installs the packages, since that step needs sudo."),
    h("div", { class: "cmd" }, h("code", null, cmd), copy),
    h(
      "p",
      { class: "dim small" },
      h("a", { href: "/install.sh" }, "Read the script first"),
      " (it's short), or run it with ",
      h("code", null, "sh -s -- --dry-run"),
      " to see what it would do. ",
      ready.length ? `Builds are out for ${ready.join(" and ")}.` : "No build is out yet, so today it says so and changes nothing.",
    ),
  );
}

function features(): HTMLElement {
  const items: [IconName, string, string][] = [
    ["library", "One library", "Every web game you love in one place, with your progress and favourites wherever you sign in."],
    ["users", "Friends and parties", "See what friends are playing, join them in a click, and send a party link that drops everyone in the same game."],
    ["play", "Press Play", "No tabs, no hunting for links. Games open in their own window and pick up where you left off."],
    ["events", "Events and clips", "Catch live events in the plaza and save the last 30 seconds of a great run."],
    ["create", "Creators first", "Follow the people behind the games and see everything they make in one place."],
    ["shield", "Games stay in their box", "Each game runs in its own locked-down process and can't reach your home network unless you allow it."],
  ];
  return h(
    "section",
    { class: "dl-features", "aria-labelledby": "dl-features-h" },
    h("h2", { id: "dl-features-h" }, "What you get"),
    h("ul", { class: "feature-grid", role: "list" }, items.map(([ic, t, d]) => h("li", null, h("span", { class: "feature-icon" }, icon(ic, 20)), h("h3", null, t), h("p", null, d)))),
  );
}

function requirements(all: Offer[]): HTMLElement {
  const rows: [string, string, string, string][] = [
    ["macOS", "macOS 13 Ventura or later", "Apple silicon (Intel later)", "About 400 MB free"],
    ["Linux", "Ubuntu 24.04 LTS or later; other apt-based systems need glibc 2.39 or newer", "x86-64", "About 450 MB free"],
    ["Windows", "Windows 10 or later", "x86-64", "Coming later"],
  ];
  const status = (name: string) => {
    const o = all.find((x) => x.label === name);
    return o && o.builds.length ? h("span", { class: "badge ok" }, "Available") : h("span", { class: "badge" }, "Coming soon");
  };
  return h(
    "section",
    { class: "dl-reqs", id: "requirements", "aria-labelledby": "dl-reqs-h" },
    h("h2", { id: "dl-reqs-h" }, "System requirements"),
    h(
      "div",
      { class: "req-grid" },
      rows.map(([name, os, cpu, disk]) =>
        h(
          "article",
          { class: "req" },
          h("div", { class: "req-head" }, h("h3", null, name), status(name)),
          h("dl", null, h("dt", null, "System"), h("dd", null, os), h("dt", null, "Processor"), h("dd", null, cpu), h("dt", null, "Disk"), h("dd", null, disk), h("dt", null, "Also"), h("dd", null, "A graphics card from the last few years and an internet connection")),
          files(all.find((x) => x.label === name)),
        ),
      ),
    ),
  );
}

/** Each system's files, so the "Also for" links (which land here) can download every build, not only yours. */
function files(o: Offer | undefined): HTMLElement | null {
  if (!o || o.builds.length === 0) return null;
  return h(
    "ul",
    { class: "req-files small", role: "list" },
    o.builds.map((b) => h("li", null, h("a", { href: b.url, download: "" }, icon("download", 14), fileName(b.url)), ` · ${formatBytes(b.bytes)} · `, h("a", { href: `${b.url}.sha256` }, "SHA-256"))),
    o.builds.some((b) => b.format === "deb") ? h("li", { class: "dim" }, "Install both packages together: sudo apt install ./banger_*.deb ./banger-runtime_*.deb") : null,
  );
}

function faq(): HTMLElement {
  const qa: [string, (string | HTMLElement)[]][] = [
    ["Is Banger free?", [freeAnswer(selling())]],
    ["Why do I need the app to play?", ["This site is for finding games, following creators and keeping up with events. Playing happens in the app, where your library, friends and parties live. Every Play button here opens it."]],
    ["Do I need an account?", ["No, you can play without one. Sign in to keep your library and progress across computers, add friends and join parties."]],
    [
      "Is the install command safe to run?",
      [
        "Piping a script into your shell means trusting it, so ",
        h("a", { href: "/install.sh" }, "read it first"),
        ". It only downloads from this site over https, refuses a file whose SHA-256 doesn't match the one published next to it, installs into your Applications folder without sudo, and prints what it did. On Linux the .deb packages need your admin password, so it prints the apt command for you to run rather than running sudo itself.",
      ],
    ],
    [
      "My Mac says Banger can't be opened. What now?",
      [
        "These early builds aren't notarised by Apple yet, so macOS warns the first time you open a copy downloaded with a browser. Installed with the terminal command above, there's no warning: curl doesn't mark the download, so macOS doesn't ask. With the disk image, drag Banger to Applications, then: on macOS 13 and 14, Control-click (or right-click) Banger in Applications, choose Open, then Open again. On macOS 15 and later, open Banger once and close the warning, then go to System Settings → Privacy & Security, scroll down to the note about Banger, click Open Anyway and confirm with your password. Or, in Terminal: ",
        h("code", null, "xattr -dr com.apple.quarantine /Applications/Banger.app"),
        ". You only do this once.",
      ],
    ],
    ["What about phones and Windows?", ["Banger runs on macOS and Linux computers first; Windows follows. On a phone you can browse, follow creators and see events here, then play at your desk."]],
    ["How do I uninstall it?", ["On macOS, move Banger from your Applications folder to the Bin. On Linux, run ", h("code", null, "sudo apt remove banger banger-runtime"), "."]],
  ];
  return h(
    "section",
    { class: "dl-faq", "aria-labelledby": "dl-faq-h" },
    h("h2", { id: "dl-faq-h" }, "Questions"),
    qa.map(([q, a]) => h("details", { class: "faq" }, h("summary", null, q), h("p", null, a))),
  );
}

/**
 * A faithful copy of the app's Home (launcher/src/ui.rs: rail, search and tabs, 16:9 hero with the featured list,
 * a poster row) at a small scale, drawn from the same catalogue art. Decorative: hidden from screen readers.
 */
function appMock(featured: Game[]): HTMLElement {
  const hero = featured[0];
  const heroArt = hero ? coverStyle(hero, "full") : {};
  const titleArt = hero ? artUrl(hero.art, "title") : null;
  const posters = featured.slice(1, 5);
  return h(
    "div",
    { class: "mock", "aria-hidden": "true" },
    h("div", { class: "mock-bar" }, h("i"), h("i"), h("i")),
    h(
      "div",
      { class: "mock-app" },
      h(
        "div",
        { class: "mock-rail" },
        h("div", { class: "mock-brand" }, h("img", { src: "/mark/banger-mark.svg", alt: "" }), h("b", null, "Banger")),
        h("div", { class: "mock-nav on" }, icon("home", 10), "Home"),
        h("div", { class: "mock-nav" }, icon("library", 10), "Library"),
        h("div", { class: "mock-rule" }),
        h("div", { class: "mock-nav" }, "+ Add game"),
        h("div", { class: "mock-nav" }, icon("users", 10), "Friends"),
      ),
      h(
        "div",
        { class: "mock-main" },
        h("div", { class: "mock-top" }, h("span", { class: "mock-search" }, icon("search", 9), "Search games"), h("span", { class: "mock-tab on" }, "Discover"), h("span", { class: "mock-tab" }, "Browse")),
        h(
          "div",
          { class: "mock-hero-row" },
          h(
            "div",
            { class: "mock-hero", style: heroArt },
            h(
              "div",
              { class: "mock-hero-text" },
              h("span", { class: "mock-eyebrow" }, hero ? `Featured · ${label(hero.genre)}` : "Featured"),
              titleArt ? h("img", { src: titleArt, alt: "" }) : h("b", null, hero?.title ?? "Banger"),
              h("span", { class: "mock-play" }, "▶ Play"),
            ),
          ),
          h(
            "div",
            { class: "mock-list" },
            featured.slice(0, 5).map((g, i) => h("div", { class: `mock-item${i === 0 ? " on" : ""}` }, h("span", { class: "mock-thumb", style: coverStyle(g) }), h("span", null, g.title))),
          ),
        ),
        h("div", { class: "mock-row-title" }, "Popular on the web ›"),
        h(
          "div",
          { class: "mock-posters" },
          (posters.length ? posters : [null, null, null, null]).map((g) => h("span", { class: "mock-poster", style: g ? coverStyle(g) : {} }, h("em", null, "Web"), h("b", null, g?.title ?? ""))),
        ),
      ),
    ),
  );
}
